import { logger } from "../utils/logger";
import { useState, useRef, useEffect, useCallback, lazy, Suspense } from 'react';
import { Link } from 'react-router';
import {
  ChevronDown, Menu, X, Video, Boxes, GraduationCap, Cog,
  BookOpen, Building2, Heart, Plane, Factory, Sprout, Zap,
  Truck, Calculator, Users, Briefcase, ArrowRight, Star,
  Play, Download, Sparkles,
} from 'lucide-react';
import { Button } from './ui/button';
import { Logo } from './Logo';
import { API_BASE_URL } from '../utils/apiConfig';
import { HeaderDynamicNav } from './HeaderDynamicNav';

// Lazy — only needed when user opens the planner
const VideoPlannerModal = lazy(() =>
  import('./VideoPlannerModal').then(m => ({ default: m.VideoPlannerModal }))
);

// ============================================
// INTERFACES
// ============================================

interface LogoConfig {
  lightMode: string;
  darkMode?: string;
  altText: string;
  width?: number;
  height?: number;
}

interface TopLevelItem {
  id: string;
  type: 'link' | 'megamenu' | 'cta';
  label: string;
  link?: string;
  visible: boolean;
  order: number;
  megamenuId?: string;
  megamenu?: any;
}

interface MegaMenuItem {
  id: string;
  type: 'link' | 'cta';
  title: string;
  description?: string;
  link?: string;
  icon?: string;
  featured?: boolean;
  badge?: string;
  tags?: string[];
  visible: boolean;
  order: number;
}

interface FooterSection {
  type: 'none' | 'cta' | 'socialproof' | 'bundle' | 'custom';
  config?: any;
}

interface MegaMenuConfig {
  id: string;
  label: string;
  headerIcon?: string;
  headerTitle: string;
  headerSubtitle?: string;
  headerGradient: string;
  items: MegaMenuItem[];
  footerSection: FooterSection;
}

// ============================================
// MODULE-LEVEL CONSTANTS
// Static data — never recreated on render.
// ============================================

// Icon lookup — module-level so getIcon doesn't
// rebuild this Record on every invocation.
const ICON_MAP: Record<string, React.ElementType> = {
  BookOpen, Calculator, Download, Users, Briefcase, Star, Video,
  Boxes, GraduationCap, Cog, Building2, Heart, Plane, Factory,
  Sprout, Zap, Truck, Sparkles, ArrowRight, Play, ChevronDown,
};

function getIcon(iconName: string | undefined, size = 'w-5 h-5'): React.ReactElement {
  const IconComponent = (iconName && ICON_MAP[iconName]) || BookOpen;
  return <IconComponent className={size} />;
}

// Default fallback for Resources mega menu
const DEFAULT_RESOURCES_DATA: MegaMenuConfig = {
  id: 'resources-fallback',
  label: 'Resources',
  headerIcon: 'BookOpen',
  headerTitle: 'Learning Hub',
  headerSubtitle: 'Expert insights for better videos',
  headerGradient: 'bg-gradient-to-r from-[#2F2E5C] via-[#413f6f] to-[#2F2E5C]',
  items: [
    {
      id: 'blog',
      type: 'link',
      title: 'Blog Articles',
      description: 'Industry insights, tips & best practices',
      link: '/blog',
      icon: 'BookOpen',
      badge: 'POPULAR',
      tags: ['50+ Articles', 'Updated Weekly'],
      visible: true,
      order: 0,
    },
    {
      id: 'roi',
      type: 'link',
      title: 'ROI Calculator',
      description: 'Calculate your video marketing ROI',
      link: '/roi',
      icon: 'Calculator',
      tags: ['Free Tool', 'Instant Results'],
      visible: true,
      order: 1,
    },
  ],
  footerSection: {
    type: 'cta',
    config: {
      icon: 'Download',
      title: 'Video Planning Template',
      description: 'Free downloadable guide to plan your next video project',
      action: 'modal',
      link: '',
    },
  },
};

// ============================================
// HELPER — transform footer section from CMS
// Declared before component so it's available
// at parse time (avoids hoisting confusion).
// ============================================

function transformFooterSection(footerSection: any): FooterSection {
  if (!footerSection?.enabled) return { type: 'none' };

  switch (footerSection.type) {
    case 'cta':
      return {
        type: 'cta',
        config: {
          icon:        footerSection.config.ctaIcon,
          title:       footerSection.config.ctaTitle,
          description: footerSection.config.ctaDescription,
          action:      footerSection.config.ctaActionType,
          link:        footerSection.config.ctaActionValue,
        },
      };
    case 'socialProof':
      return {
        type: 'socialproof',
        config: {
          rating:      footerSection.config.spRating,
          clientCount: footerSection.config.spClientCount,
          description: footerSection.config.spDescription,
          label:       footerSection.config.spLabel,
        },
      };
    default:
      return { type: 'none' };
  }
}

// ============================================
// NAV SKELETON — shown while menu config loads
// ============================================

function NavSkeleton() {
  return (
    <div className="hidden lg:flex items-center space-x-2">
      {[80, 100, 90, 80, 110].map((w, i) => (
        <div
          key={i}
          className="h-8 bg-gray-200 rounded-full relative overflow-hidden"
          style={{ width: w }}
        >
          <div className="absolute inset-0 shimmer" />
        </div>
      ))}
    </div>
  );
}

// ============================================
// COMPONENT
// ============================================

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen]     = useState(false);
  const [activeMenu, setActiveMenu]             = useState<string | null>(null);
  const [isPlannerModalOpen, setIsPlannerModalOpen] = useState(false);
  const [logo, setLogo]                         = useState<LogoConfig | null>(null);
  const [logoLoaded, setLogoLoaded]             = useState(false);
  const [menuItems, setMenuItems]               = useState<TopLevelItem[]>([]);
  const [megaMenus, setMegaMenus]               = useState<Record<string, MegaMenuConfig>>({});
  const [menuLoading, setMenuLoading]           = useState(true);

  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // ============================================
  // FETCH MENU CONFIG
  // ============================================

  const fetchMenuConfig = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/menu/published`);
      const data = await response.json();

      if (data.success && data.data?.configData) {
        const cfg = data.data.configData;

        if (cfg.logo) setLogo(cfg.logo);

        if (cfg.topLevelItems) {
          setMenuItems(cfg.topLevelItems);

          const extractedMegaMenus: Record<string, MegaMenuConfig> = {};
          cfg.topLevelItems.forEach((item: TopLevelItem) => {
            if (item.type === 'megamenu' && item.megamenu) {
              extractedMegaMenus[item.id] = {
                id: item.id,
                label: item.label,
                headerIcon:     item.megamenu.header.icon,
                headerTitle:    item.megamenu.header.title,
                headerSubtitle: item.megamenu.header.subtitle,
                headerGradient:
                  item.megamenu.header.colorTheme === 'red'
                    ? 'bg-gradient-to-r from-[#D74444] via-[#E87D3E] to-[#D74444]'
                    : 'bg-gradient-to-r from-[#2F2E5C] via-[#413f6f] to-[#2F2E5C]',
                items: item.megamenu.items.map((menuItem: any) => ({
                  id:          menuItem.id,
                  type:        menuItem.type,
                  title:       menuItem.title,
                  description: menuItem.description,
                  link:        menuItem.link,
                  icon:        menuItem.icon,
                  featured:    menuItem.layout === 'featured',
                  badge:       menuItem.badge?.enabled ? menuItem.badge.text : undefined,
                  tags:        menuItem.metaTags?.map((tag: any) => tag.text) || [],
                  visible:     true,
                  order:       menuItem.order,
                })),
                footerSection: transformFooterSection(item.megamenu.footerSection),
              };
            }
          });
          setMegaMenus(extractedMegaMenus);
        }
      }
    } catch (error) {
      logger.error('❌ Error fetching menu config:', error);
    } finally {
      setMenuLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMenuConfig();
  }, [fetchMenuConfig]);

  // Cleanup hover timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  // ============================================
  // HOVER HANDLERS
  // ============================================

  const handleMenuEnter = (menu: string) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveMenu(menu);
  };

  const handleMenuLeave = () => {
    closeTimeoutRef.current = setTimeout(() => setActiveMenu(null), 150);
  };

  // ============================================
  // DERIVED VALUES
  // ============================================

  // Resources mega menu — CMS first, fallback second
  const resourcesMenuItem = menuItems.find(
    item => item.type === 'megamenu' && item.label === 'Resources'
  );
  const resourcesMenuData = (resourcesMenuItem && megaMenus[resourcesMenuItem.id])
    || DEFAULT_RESOURCES_DATA;

  // ============================================
  // RENDER
  // ============================================

  return (
    <header className="bg-white/95 backdrop-blur-xl border-b border-gray-200 sticky top-0 z-50 shadow-lg">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">

          {/* Logo — show <Logo> fallback until CMS URL is available AND image has loaded */}
          <Link to="/" className="flex items-center group">
            {logo?.lightMode ? (
              <>
                {/* Hidden image preload — reveals once loaded */}
                <img
                  src={logo.lightMode}
                  alt={logo.altText || 'Company Logo'}
                  className={`h-10 w-auto transition-all group-hover:scale-105 ${logoLoaded ? 'block' : 'hidden'}`}
                  style={{
                    width:     logo.width  ? `${logo.width}px`  : 'auto',
                    height:    logo.height ? `${logo.height}px` : '40px',
                    maxHeight: '40px',
                  }}
                  onLoad={() => setLogoLoaded(true)}
                  onError={() => setLogoLoaded(false)}
                />
                {/* Fallback shown until image loads */}
                {!logoLoaded && (
                  <Logo className="h-10 w-auto transition-transform group-hover:scale-105" />
                )}
              </>
            ) : (
              <Logo className="h-10 w-auto transition-transform group-hover:scale-105" />
            )}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-2">
            {menuLoading ? (
              <NavSkeleton />
            ) : (
              <HeaderDynamicNav
                menuItems={menuItems}
                megaMenus={megaMenus}
                activeMenu={activeMenu}
                onMenuEnter={handleMenuEnter}
                onMenuLeave={handleMenuLeave}
                onPlannerModalOpen={() => setIsPlannerModalOpen(true)}
                getIcon={getIcon}
              />
            )}
          </nav>

          {/* Mobile menu button */}
          <button
            className="lg:hidden text-gray-700"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu — driven from CMS menuItems */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100">
          <div className="px-4 py-6 space-y-2 max-h-[calc(100vh-5rem)] overflow-y-auto">
            {menuLoading ? (
              /* Skeleton while loading */
              <div className="space-y-3">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-9 bg-gray-100 rounded-lg relative overflow-hidden">
                    <div className="absolute inset-0 shimmer" />
                  </div>
                ))}
              </div>
            ) : (
              <>
                {menuItems
                  .filter(item => item.visible)
                  .sort((a, b) => a.order - b.order)
                  .map(item => {
                    if (item.type === 'link' && item.link) {
                      return (
                        <Link
                          key={item.id}
                          to={item.link}
                          className="block text-gray-700 hover:text-[#D74444] py-2 font-medium"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {item.label}
                        </Link>
                      );
                    }

                    if (item.type === 'megamenu') {
                      const menu = megaMenus[item.id];
                      if (!menu) return null;
                      return (
                        <div key={item.id} className="pt-4 border-t border-gray-100 first:border-t-0 first:pt-0">
                          <div className="font-bold text-[#2F2E5C] mb-3 text-sm">{item.label}</div>
                          <div className="space-y-1 pl-2">
                            {menu.items
                              .filter(mi => mi.visible)
                              .sort((a, b) => a.order - b.order)
                              .map(mi => {
                                if (mi.type === 'link' && mi.link) {
                                  return (
                                    <Link
                                      key={mi.id}
                                      to={mi.link}
                                      className="block text-gray-700 hover:text-[#D74444] text-sm py-2"
                                      onClick={() => setMobileMenuOpen(false)}
                                    >
                                      {mi.title}
                                    </Link>
                                  );
                                }
                                if (mi.type === 'cta') {
                                  return (
                                    <button
                                      key={mi.id}
                                      onClick={() => {
                                        setIsPlannerModalOpen(true);
                                        setMobileMenuOpen(false);
                                      }}
                                      className="block text-gray-700 hover:text-[#D74444] text-sm py-2 text-left w-full"
                                    >
                                      {mi.title}
                                    </button>
                                  );
                                }
                                return null;
                              })}
                          </div>
                        </div>
                      );
                    }

                    if (item.type === 'cta' && item.link) {
                      return (
                        <Link
                          key={item.id}
                          to={item.link}
                          className="block bg-gradient-to-r from-[#D74444] to-[#c13939] text-white px-6 py-3 text-center rounded-full font-medium mt-4"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {item.label}
                        </Link>
                      );
                    }

                    return null;
                  })}
              </>
            )}
          </div>
        </div>
      )}

      {/* Video Planner Modal — lazy loaded */}
      <Suspense fallback={null}>
        <VideoPlannerModal
          isOpen={isPlannerModalOpen}
          onClose={() => setIsPlannerModalOpen(false)}
        />
      </Suspense>
    </header>
  );
}